// frontend-react/src/services/validationService.ts
import apiClient from './apiClient';
import type { ReviewItem } from '../types';
import { generateReviewCsvString } from '../utils/csvUtils';

// ── 批次驗測型別 ──

export interface BatchGroupResult {
  idx: number;
  name: string;
  recall: number;
  false_positive_rate: number;
  correct: number;
  missing: number;
  extra: number;
}

export interface BatchValidationResult {
  batch_id: string;
  message: string;
  groups: BatchGroupResult[];
}

/**
 * 批次驗測：上傳多組（法務 DOCX + 待測文件），後端逐組跑 AI 審閱並計算準確率。
 * @param ruleSetId - 指定規則集 ID；不傳則後端使用公司預設規則集
 */
export async function batchValidate(
  groups: { legalDoc: File; originalFile: File; name: string }[],
  ruleSetId?: number | null
): Promise<BatchValidationResult> {
  const formData = new FormData();
  groups.forEach(g => {
    formData.append('legal_docs', g.legalDoc);
    formData.append('original_files', g.originalFile);
  });
  formData.append('group_names', JSON.stringify(groups.map(g => g.name)));
  if (ruleSetId != null) {
    formData.append('rule_set_id', String(ruleSetId));
  }
  const resp = await apiClient.post<BatchValidationResult>('/api/v1/validation/batch', formData);
  return resp.data;
}

/**
 * 下載批次驗測中單組的結果檔案。
 */
export async function downloadBatchGroupFile(
  batchId: string,
  groupIdx: number,
  fileType: 'word' | 'excel',
  downloadName?: string
): Promise<void> {
  const name =
    downloadName ??
    (fileType === 'word' ? `validation_${groupIdx + 1}.docx` : `validation_${groupIdx + 1}.xlsx`);
  const resp = await apiClient.get(
    `/api/v1/validation/batch/${batchId}/group/${groupIdx}/${fileType}`,
    { responseType: 'blob' }
  );
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * 下載批次驗測的綜合報表 Excel（含公式連動統計表）。
 */
export async function downloadBatchCombinedExcel(batchId: string, filename = 'validation_batch.xlsx'): Promise<void> {
  const resp = await apiClient.get(`/api/v1/validation/batch/${batchId}/combined-excel`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ValidationResult {
  task_id: string;
  message: string;
  recall: number; // 召回率（0~100）= 正確 ÷ (正確 + 遺漏)
  false_positive_rate: number; // 誤報率（0~100）= 多抓 ÷ (正確 + 多抓)
}

/**
 * 送出法務標準答案 DOCX + 目前審閱結果，執行準確率驗測。
 * 後端回傳 task_id，再用 downloadValidationFile 下載結果。
 */
export async function evaluateAccuracy(
  docxFile: File,
  activeResults: ReviewItem[]
): Promise<ValidationResult> {
  const csvData = generateReviewCsvString(activeResults);
  const formData = new FormData();
  formData.append('docx_file', docxFile);
  formData.append('csv_data', csvData);

  const resp = await apiClient.post<ValidationResult>(
    '/api/v1/validation/evaluate',
    formData
    // 不設 Content-Type，讓瀏覽器自動加 multipart boundary
  );
  return resp.data;
}

/**
 * 下載驗測結果檔案並觸發瀏覽器下載。
 * @param taskId - evaluate 回傳的 task_id
 * @param fileType - 'word' | 'excel'
 * @param downloadName - 下載時顯示的檔名
 */
export async function downloadValidationFile(
  taskId: string,
  fileType: 'word' | 'excel',
  downloadName: string
): Promise<void> {
  const resp = await apiClient.get(`/api/v1/validation/download/${taskId}/${fileType}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
